import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { ConfirmDialog, type ConfirmDialogState } from './ConfirmDialog';
import { ColorPicker } from './ColorPicker';

interface CalendarQuickMenuProps {
  isOpen: boolean;
  onClose: () => void;
  calendar: {
    id: string;
    name: string;
    url: string;
    color: string;
  };
  onUpdate: (id: string, updates: { name?: string; url?: string; color?: string }) => Promise<any>;
  onDelete: (id: string) => Promise<void>;
  onSync: (id: string) => Promise<void>;
  syncing: boolean;
  anchorElement: HTMLButtonElement;
}

export default function CalendarQuickMenu({
  isOpen,
  onClose,
  calendar,
  onUpdate,
  onDelete,
  onSync,
  syncing,
  anchorElement,
}: CalendarQuickMenuProps) {
  const [name, setName] = useState(calendar.name);
  const [color, setColor] = useState(calendar.color);
  const [showUrlEdit, setShowUrlEdit] = useState(false);
  const [url, setUrl] = useState(calendar.url);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset state and capture position when menu opens
  useEffect(() => {
    if (isOpen && anchorElement) {
      setName(calendar.name);
      setColor(calendar.color);
      setUrl(calendar.url);
      setShowUrlEdit(false);
      
      const rect = anchorElement.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = 360;
      const gap = 8;
      
      let top = rect.top;
      let left = rect.right + gap;
      
      if (left + menuWidth > window.innerWidth - 8) {
        left = rect.left - menuWidth - gap;
      }
      
      if (top + menuHeight > window.innerHeight - 8) {
        top = window.innerHeight - menuHeight - 8;
      }
      if (top < 8) top = 8;
      
      setMenuPosition({ top, left });
    }
  }, [calendar.id, calendar.name, calendar.color, calendar.url, isOpen, anchorElement]);

  // Focus name input when opening
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
          const len = nameInputRef.current.value.length;
          nameInputRef.current.setSelectionRange(len, len);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const saveChanges = async (updates: { name?: string; color?: string; url?: string }) => {
    await onUpdate(calendar.id, updates);
  };

  const handleNameBlur = () => {
    if (name !== calendar.name && name.trim()) {
      saveChanges({ name: name.trim() });
    }
  };

  const handleUrlBlur = () => {
    if (url !== calendar.url && url.trim()) {
      saveChanges({ url: url.trim() });
    }
  };

  const handleColorSelect = (newColor: string) => {
    setColor(newColor);
    saveChanges({ color: newColor });
  };

  const handleDelete = () => {
    setConfirmDialog({
      title: 'Delete Calendar',
      message: `Delete "${calendar.name}"? All events from this calendar will be removed.`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        await onDelete(calendar.id);
        onClose();
      },
    });
  };

  const getMenuStyle = (): React.CSSProperties => {
    if (!menuPosition) return { top: 0, left: 0 };
    return menuPosition;
  };

  return (
    <>
      {/* Invisible backdrop to catch outside clicks */}
      <div
        className="fixed inset-0 z-[99]"
        onMouseDown={(e) => {
          if (menuRef.current && menuRef.current.contains(e.target as Node)) {
            return;
          }
          // Don't close if a confirm dialog is open
          if (confirmDialog) return;
          onClose();
        }}
      />
      
      {/* Menu */}
      <div
        ref={menuRef}
        className="fixed bg-[var(--modal-bg)] rounded-lg shadow-lg border border-[var(--border-primary)] w-56 z-[100] animate-fadeIn"
        style={getMenuStyle()}
      >
        {/* Name input */}
        <div className="p-2 border-b border-[var(--border-secondary)]">
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleNameBlur();
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-full px-2 py-1 text-sm border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
            placeholder="Calendar name"
          />
        </div>

        {/* Color picker */}
        <div className="p-2 border-b border-[var(--border-secondary)]">
          <div className="text-[10px] text-[var(--content-secondary)] uppercase tracking-wide mb-1.5">Color</div>
          <ColorPicker
            value={color}
            onChange={handleColorSelect}
          />
        </div>

        {/* URL (collapsible) — hidden for Google Calendar and file imports */}
        {calendar.url !== 'google-calendar-sync' && calendar.url !== 'file://imported' && (
          <div className="p-2 border-b border-[var(--border-secondary)]">
            <button
              type="button"
              onClick={() => setShowUrlEdit(!showUrlEdit)}
              className="text-[10px] text-[var(--content-secondary)] uppercase tracking-wide hover:text-[var(--content-primary)] cursor-pointer"
            >
              {showUrlEdit ? '▼ URL' : '▶ URL'}
            </button>
            {showUrlEdit && (
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={handleUrlBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUrlBlur();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                className="w-full mt-1 px-2 py-1 text-xs border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                placeholder="ICS URL"
              />
            )}
          </div>
        )}

        {/* Actions */}
        <div className="p-1">
          {calendar.url !== 'file://imported' && (
            <button
              type="button"
              onClick={() => onSync(calendar.id)}
              disabled={syncing}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync now'}
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--error)] hover:bg-[var(--status-error-bg)] rounded transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete calendar
          </button>
        </div>
      </div>
      {confirmDialog && (
        <ConfirmDialog dialog={confirmDialog} onClose={() => setConfirmDialog(null)} />
      )}
    </>
  );
}